import Box from '@components/base/Box'
import { Button } from '@components/inputs/Button'
import { DropdownInput } from '@components/inputs/DropdownInput'
import { PriceInput } from '@components/inputs/PriceInput'
import { TextInput } from '@components/inputs/TextInput'
import Modal from '@components/base/Modal'
import {
  DiscountType,
  EventDetails,
  EventPaymentProcessor,
  EventTicket,
  EventTicketDiscount,
  TicketType,
} from '@graphql/__generated/graphql'
import CreateEventLayout from '@layouts/CreateEventLayout'
import { useEventEditor } from '@libs/CreateEvent/useEventEditor'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Table from '@components/base/Table'
import TableRowActions from '@components/event/TableRowActions'
import currencies from '@libs/currencies'
import { ItemPicker } from '@components/inputs/ItemPicker'
import { parseInt } from 'lodash'
import { ArrayTextInput } from '@components/inputs/ArrayTextInput'
import RegistrationFieldModal, { FieldModalData } from './registrationFields'
import DiscountModal, {
  DiscountLinkModal,
  DiscountModalData,
} from './discounts'
import { ToggleInput } from '@components/inputs/ToggleInput'
import { useTheme } from '@libs/useTheme'
import { isGoCardlessEnabled } from '@libs/paymentFeatureFlags'

const TicketsForm = ({ currency }: { currency: string }) => {
  const [showTicketModal, setShowTicketModal] = useState(false)
  const [showFieldModal, setShowFieldModal] = useState(false)
  const [showDiscountModal, setShowDiscountModal] = useState(false)
  const [showLinkModal, setShowLinkModal] = useState(false)

  const {
    eventActionsVisible,
    formData,
    goCardlessCurrencySupported,
    isAdmin,
    paymentSettings,
    setFormData,
    paymentSelectionReady,
    paymentSettingsLoading,
    selectedPaymentProcessor,
  } = useEventEditor()
  const { theme } = useTheme()

  const currencyDetails = currencies[currency.toUpperCase()]

  const [ticketModalData, setTicketModalData] = useState<
    Partial<EventTicket & { editIndex?: number }> | undefined
  >(undefined)

  const [discountModalData, setDiscountModalData] = useState<
    DiscountModalData | undefined
  >(undefined)

  const [fieldModalData, setFieldModalData] = useState<
    FieldModalData | undefined
  >(undefined)

  const { registrationFields } = formData ?? {}

  const tickets = useMemo(() => {
    const payload: (Omit<EventTicket, 'adminFee'> & { remaining: number })[] =
      []

    formData?.tickets?.forEach((t) => {
      const availableTicket = formData.availableTickets?.tickets?.find(
        (at) => at.ticketTitle === t.title
      )
      payload.push({
        ...t,
        remaining: availableTicket?.remaining
          ? parseInt(availableTicket?.remaining)
          : 0,
      })
    })

    return payload
  }, [formData?.tickets, formData?.availableTickets])

  const discounts = useMemo(() => {
    const payload: (EventTicketDiscount & { remaining: number })[] = []

    formData?.discounts?.forEach((dc) => {
      const availableTicket = formData.availableTickets?.tickets?.find((at) =>
        at.discountCodes?.map((c) => c.title).includes(dc.title)
      )

      const discount = availableTicket?.discountCodes?.find(
        (f) => f.title.toUpperCase() === dc.title.toUpperCase()
      )

      payload.push({
        ...dc,
        remaining:
          discount?.remaining ?? parseInt(availableTicket?.remaining ?? '0'),
      })
    })

    return payload
  }, [formData?.discounts, formData?.availableTickets])

  useEffect(() => {
    const payload = {
      ...formData,
      event: { ...formData?.event, currency: currency } as EventDetails,
    }
    if (formData?.event?.currency !== currency) setFormData(payload)
    // eslint-disable-next-line
  }, [currency])

  const noTickets = tickets == undefined || tickets.length === 0
  const hasPaidTickets = tickets.some(
    (ticket) =>
      ticket.ticketType === TicketType.Paid ||
      ticket.ticketType === TicketType.GroupGuest
  )
  const groupGuestTicketIndex = tickets.findIndex(
    (ticket) => ticket.ticketType === TicketType.GroupGuest
  )
  const noFields =
    registrationFields == undefined || registrationFields.length === 0
  const noDiscounts = discounts == undefined || discounts.length === 0

  const addTicketModal = (
    <Modal
      show={showTicketModal}
      setShow={setShowTicketModal}
      title={`${
        ticketModalData?.editIndex !== undefined ||
        ticketModalData?.editIndex === 0
          ? 'Edit'
          : 'Create'
      } Ticket`}
    >
      <TextInput
        validations={{
          minLength: {
            value: 1,
            message: 'Ticket name must be at least 1 character',
          },
          maxLength: {
            value: 70,
            message: 'Ticket name must be at most 70 characters',
          },
        }}
        required
        label="Title"
        placeholder="VIP Ticket"
        value={ticketModalData?.title}
        onChange={(data) => {
          setTicketModalData({ ...ticketModalData, title: data })
        }}
      />
      <TextInput
        required
        label="Available Tickets"
        placeholder="50"
        type="number"
        value={ticketModalData?.quantity}
        validations={{
          minLength: {
            value: 1,
            message: 'Tickets available must be at least 1 ',
          },
          maxLength: {
            value: 4,
            message: 'Tickets available must be at most 9999',
          },
        }}
        onChange={(data) => {
          setTicketModalData({ ...ticketModalData, quantity: data })
        }}
      />
      <DropdownInput
        label="Ticket Type"
        options={[
          { value: TicketType.Paid, label: 'Paid' },
          { value: TicketType.Free, label: 'Free' },
          { value: TicketType.InvoiceTicket, label: 'Invoice Ticket' },
          { value: TicketType.WaitingList, label: 'Waiting List' },
          ...(isAdmin || ticketModalData?.ticketType === TicketType.GroupGuest
            ? [
                {
                  value: TicketType.GroupGuest,
                  label: 'Group guest',
                  disabled: !isAdmin,
                },
              ]
            : []),
        ]}
        disabled={
          !isAdmin && ticketModalData?.ticketType === TicketType.GroupGuest
        }
        required
        placeholder="Select a ticket type"
        onChange={(data) => {
          setTicketModalData({ ...ticketModalData, ticketType: data })
        }}
        value={ticketModalData?.ticketType}
      />
      <Box
        show={
          ticketModalData?.ticketType === TicketType.Paid ||
          ticketModalData?.ticketType === TicketType.InvoiceTicket ||
          ticketModalData?.ticketType === TicketType.GroupGuest
        }
      >
        <PriceInput
          required
          label="Price"
          currencySymbol={currencyDetails.symbol}
          value={ticketModalData?.price}
          onChange={(data) => {
            setTicketModalData({
              ...ticketModalData,
              price: data,
            })
          }}
          placeholder="14.99"
        />
      </Box>

      <ToggleInput
        errorMessage={undefined}
        selected={ticketModalData?.allowMultiplePurchase ? 0 : 1}
        centerText
        options={['Yes', 'No']}
        className={`mb-4`}
        value={ticketModalData?.allowMultiplePurchase ? 'Yes' : 'No'}
        callback={(val) => {
          setTicketModalData({
            ...ticketModalData,
            allowMultiplePurchase: val === 'Yes' ? true : false,
          })
        }}
        label={'Allow users to purchase multiple tickets'}
      />

      <Box className="flex justify-between items-center w-full">
        {/* <Button */}
        {/*   className="my-4" */}
        {/*   title={'Back'} */}
        {/*   type="primary" */}
        {/* /> */}
        <Button
          className="my-4 ml-auto"
          title={`${
            ticketModalData?.editIndex !== undefined ||
            ticketModalData?.editIndex === 0
              ? 'Edit Ticket'
              : 'Create Ticket'
          }`}
          type="primary"
          onClick={(e) => {
            e.preventDefault()
            console.log({ S: ticketModalData })
            const ticketData: Omit<EventTicket, 'adminFee'> = {
              title: ticketModalData?.title ?? '',
              ticketType: ticketModalData?.ticketType ?? TicketType.Free,
              price:
                ticketModalData?.ticketType === TicketType.Paid ||
                ticketModalData?.ticketType === TicketType.InvoiceTicket ||
                ticketModalData?.ticketType === TicketType.GroupGuest
                  ? parseFloat(ticketModalData?.price?.toString() ?? '0.00')
                  : 0,
              quantity: parseInt(ticketModalData?.quantity?.toString() ?? '0'),
              allowMultiplePurchase:
                ticketModalData?.allowMultiplePurchase ?? true,
            }

            const payload = [...(formData?.tickets ?? [])]

            if (ticketModalData?.editIndex || ticketModalData?.editIndex === 0)
              payload[ticketModalData.editIndex] = ticketData
            else payload.push(ticketData)

            setFormData({
              ...formData,
              tickets: payload,
            })

            setShowTicketModal(false)
            setTicketModalData(undefined)
          }}
        />
      </Box>
      {/* <Box> */}

      {/* </Box> */}
    </Modal>
  )
  // useEffect(() => {
  //   console.log({ discounts })
  // }, [discounts])

  return (
    <>
      {addTicketModal}
      <RegistrationFieldModal
        show={showFieldModal}
        setShow={setShowFieldModal}
        initialData={fieldModalData}
      />
      <DiscountModal
        show={showDiscountModal}
        setShow={setShowDiscountModal}
        initialData={discountModalData}
        tickets={tickets}
      />
      <DiscountLinkModal show={showLinkModal} setShow={setShowLinkModal} />
      <Box className="w-full">
        <Box className="flex items-center space-x-2 mb-4 mt-6">
          <Box className="text-xl">Tickets List</Box>
          <Button
            onClick={() => {
              setTicketModalData({ allowMultiplePurchase: true })
              setShowTicketModal(true)
            }}
            title="Add Ticket"
            type="secondary"
            size="small"
          />
        </Box>
        <Table
          tableHeading={[
            'Title',
            'Ticket Type',
            'Price',
            'Quantity',
            'Available',
            '',
          ]}
          sortable={
            isAdmin ||
            !tickets.some(
              (ticket) => ticket.ticketType === TicketType.GroupGuest
            )
          }
          onSort={(d) => {
            const payload = {
              ...formData,
              tickets: d.map((m) => {
                try {
                  delete m.remaining
                } catch (_) {}
                return m
              }),
            }
            setFormData(payload)
          }}
          items={tickets}
          rows={tickets.map((ticket, index) => [
            ticket.title,
            ticket.ticketType,
            `${currencyDetails.symbol}${ticket.price!.toFixed(2)}`,
            ticket.quantity,
            !!ticket?.remaining || ticket.remaining === 0
              ? ticket.remaining
              : 'N/A',
            <TableRowActions
              key={index}
              editClicked={() => {
                setTicketModalData({
                  title: ticket.title,
                  ticketType: ticket.ticketType,
                  quantity: ticket.quantity,
                  price: ticket.price,
                  allowMultiplePurchase: ticket.allowMultiplePurchase,
                  editIndex: index,
                })
                setShowTicketModal(true)
              }}
              deleteClicked={
                !isAdmin &&
                groupGuestTicketIndex >= 0 &&
                index <= groupGuestTicketIndex
                  ? undefined
                  : () => {
                      setFormData({
                        ...formData,
                        tickets: formData?.tickets?.filter(
                          (_, i) => i !== index
                        ),
                      })
                    }
              }
            />,
          ])}
        />
        <Box show={noTickets}>
          <Box className="text-sm mt-8 text-center mb-8">
            {'No tickets have been created yet'}
          </Box>
        </Box>
        <Box
          show={!!eventActionsVisible && hasPaidTickets}
          className="mt-4 flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between"
          style={{
            backgroundColor: theme.backgroundColourSecondary,
            borderColor: theme.borderColour,
            color: theme.textColour,
          }}
        >
          <Box>
            <label
              className="font-semibold"
              htmlFor="event-payment-processor"
            >
              Payment processor
            </label>
            <Box
              id="event-payment-processor-description"
              className="mt-1 text-sm opacity-80"
            >
              One processor handles new checkouts. You can change it while the
              event is live.
            </Box>
          </Box>
          <select
            id="event-payment-processor"
            aria-describedby="event-payment-processor-description"
            className="min-w-full cursor-pointer rounded-md border px-3 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forumm-blue sm:min-w-[220px]"
            style={{
              backgroundColor: theme.backgroundColour,
              borderColor: theme.borderColour,
              color: theme.textColour,
            }}
            value={selectedPaymentProcessor}
            onChange={(event) =>
              setFormData({
                ...formData,
                paymentProcessor: event.target.value as EventPaymentProcessor,
              })
            }
          >
            <option
              value="STRIPE"
              disabled={!paymentSettings?.stripe?.ready}
              style={{
                backgroundColor: theme.backgroundColour,
                color: theme.textColour,
              }}
            >
              Stripe
              {paymentSettings?.stripe?.ready ? '' : ' (not ready)'}
            </option>
            {isGoCardlessEnabled() && (
              <option
                value="GOCARDLESS"
                disabled={
                  !paymentSettings?.gocardless?.ready ||
                  !goCardlessCurrencySupported
                }
                style={{
                  backgroundColor: theme.backgroundColour,
                  color: theme.textColour,
                }}
              >
                GoCardless
                {!goCardlessCurrencySupported
                  ? ' (GBP/EUR only)'
                  : paymentSettings?.gocardless?.ready
                    ? ''
                    : ' (not ready)'}
              </option>
            )}
            <option
              value="PAYMENTS_DISABLED"
              style={{
                backgroundColor: theme.backgroundColour,
                color: theme.textColour,
              }}
            >
              Payments disabled
            </option>
          </select>
        </Box>
        <Box
          show={hasPaidTickets && !paymentSelectionReady}
          className="mt-4 rounded-lg border border-forumm-orange bg-forumm-orange/10 p-4 text-sm"
        >
          <Box className="font-semibold">Payment setup is required</Box>
          <Box className="mt-1">
            Paid tickets cannot be published until the selected processor is
            connected and ready in organisation settings.
            {paymentSettingsLoading ? ' Checking your current status...' : ''}
          </Box>
        </Box>

        <Box className="flex items-center space-x-2 mt-12">
          <Box className="text-xl">Discount Codes</Box>
          <Button
            onClick={() => {
              setDiscountModalData(undefined)
              setShowDiscountModal(true)
            }}
            title="Add Discount"
            type="secondary"
            size="small"
          />
          <Button
            onClick={() => {
              setShowLinkModal(true)
            }}
            title="Discount Link"
            type="secondary"
            size="small"
          />
        </Box>
        <Box className="mt-4 mb-4 text-sm">
          Want to provide discounts to your attendees? Create a discount code
          and assign it to a ticket.
        </Box>
        <Table
          tableHeading={[
            'Name',
            'Type',
            'Quantity',
            'Remaining',
            'Percentage',
            'Tickets',
            '',
          ]}
          sortable
          items={discounts}
          onSort={(d) => {
            const payload = {
              ...formData,
              discounts: d.map((m) => {
                try {
                  delete m.remaining
                } catch (_) {}
                return m
              }),
            }
            setFormData(payload)
          }}
          rows={discounts?.map((discount, index) => {
            return [
              discount.title,
              discount.type === DiscountType.Percentage ? '% Off' : '2-4-1',
              discount.quantity,
              discount.remaining,
              !!discount.percentage ? `${discount.percentage}%` : '',
              discount.tickets?.join(', ') ?? '',
              <Box key={index} className={`flex justify-between items-center`}>
                <TableRowActions
                  confirmModal={{
                    title: 'Are you sure?',
                    content:
                      'Are you sure you want to delete this? This cannot be undone.',
                  }}
                  editClicked={() => {
                    console.log({ click: index })
                    setDiscountModalData({
                      ...discount,
                      editIndex: index,
                    })
                    setShowDiscountModal(true)
                  }}
                  deleteClicked={() => {
                    console.log({ click: index })
                    setFormData({
                      ...formData,
                      discounts: discounts?.filter((_, i) => i !== index),
                    })
                  }}
                />
              </Box>,
            ]
          })}
        />
        <Box show={noDiscounts}>
          <Box className="text-sm mt-8 text-center mb-8">
            {'No discounts have been created'}
          </Box>
          <hr className="my-6 border-gray-600 opacity-50" />
        </Box>

        <Box className="flex items-center space-x-2 mt-12">
          <Box className="text-xl">Registration Details</Box>
          <Button
            onClick={() => {
              setFieldModalData(undefined)
              setShowFieldModal(true)
            }}
            title="Add Field"
            type="secondary"
            size="small"
          />
        </Box>
        <Box className="mt-4 mb-4 text-sm">
          Need to capture some information from your attendees? Add information
          fields that you wish to capture from attendees
        </Box>
        <Box className="my-2 font-bold">
          All attendees will be asked for their name and email address.
        </Box>
        <Table
          tableHeading={['Field Name', '']}
          sortable
          onSort={(d) => {
            const payload = {
              ...formData,
              registrationFields: d,
            }
            setFormData(payload)
          }}
          items={registrationFields ?? []}
          rows={registrationFields?.map((field, index) => [
            field.name,
            <Box key={index} className={`flex justify-between items-center`}>
              <Box className="text-sm flex flex-col" ignoreTheme>
                <Box className="text-sm" ignoreTheme>
                  {'Type: '}
                  <span className="text-gray-500 text-xs">
                    {field.options && field.options.length > 0
                      ? `${field.type} (${field.options.length})`
                      : field.type}
                  </span>
                </Box>
                <Box show={!!field.unique} className="text-sm" ignoreTheme>
                  {'Unique Answer: '}
                  <span className="text-gray-500 text-xs">
                    {field.unique ? 'Yes' : 'No'}
                  </span>
                </Box>
              </Box>
              <TableRowActions
                confirmModal={{
                  title: 'Are you sure?',
                  content:
                    'Are you sure you want to delete this? This cannot be undone.',
                }}
                editClicked={() => {
                  setFieldModalData({
                    ...field,
                    editIndex: index,
                  })
                  setShowFieldModal(true)
                }}
                deleteClicked={() => {
                  setFormData({
                    ...formData,
                    registrationFields: registrationFields?.filter(
                      (_, i) => i !== index
                    ),
                  })
                }}
              />
            </Box>,
          ])}
        />
        <Box show={noFields}>
          <Box className="text-sm mt-8 text-center mb-8">
            {'No custom fields have been created'}
          </Box>
          <hr className="my-6 border-gray-600 opacity-50" />
        </Box>
      </Box>
    </>
  )
}

TicketsForm.Layout = CreateEventLayout

export default TicketsForm
